import { useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import {
  useAddUserMutation,
  useLazyGetUserValidationQuery,
} from "../../../redux/api/user/userAPI";
import { setIsNewUser } from "../../../redux/features/auth/auth-slice";
import useDebounce from "../../../shared/hooks/useDebounce";
import RegisteringUsersDTO from "../../../shared/oversight-core/dtos/request-dtos/registering-users-dto";
import CheckUserAvailabilityResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/check-user-availability-response-dto";
import { AppRoute } from "../../../shared/oversight-core/interfaces/app-routes";
import AppButton from "../../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import AppDualInput from "../../../shared/oversight-core/ui-elements/input/app-dual-input/app-dual-input";
import AppInput from "../../../shared/oversight-core/ui-elements/input/app-input";
import {
  emailRegex,
  noSpecialCharsNotAllowOnlyNumbers,
  passwordRegex,
} from "../../../shared/oversight-core/utils/regex";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../shared/oversight-core/utils/toast";

interface IFormInput extends RegisteringUsersDTO {
  termsAndConditions: boolean;
  confirmPassword: string;
}

const defaultFormValues: IFormInput = {
  firstName: "",
  lastName: "",
  username: "",
  email: "",
  mobileNumber: "",
  password: "",
  confirmPassword: "",
  NIC: "",
  termsAndConditions: false,
};

const RegistrationForm = () => {
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorStyle, setErrorStyle] = useState(false);
  const [emailError, setEmailError] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<IFormInput>({
    defaultValues: { ...defaultFormValues },
  });

  const password = useRef({});
  password.current = watch("password", "");
  const email = watch("email");

  const [AddUser, { isLoading: isLoadingAddUser }] = useAddUserMutation();
  const [triggerGetUserValidation] = useLazyGetUserValidationQuery();

  const debouncedEmail = useDebounce(email, 1000);

  useEffect(() => {
    if (debouncedEmail && emailRegex.test(debouncedEmail)) {
      triggerGetUserValidation({
        email: debouncedEmail,
      })
        .unwrap()
        .then((response: CheckUserAvailabilityResponseDTO) => {
          setEmailError(!response.userAvailability.email.CEB);
        })
        .catch(() => {
          setEmailError(true);
        });
    }
  }, [triggerGetUserValidation, debouncedEmail]);

  useEffect(() => {
    if (!emailError) {
      triggerGetUserValidation({
        email: debouncedEmail,
      })
        .unwrap()
        .then((response: CheckUserAvailabilityResponseDTO) => {
          setEmailError(response.userAvailability.email.LECO);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [triggerGetUserValidation, emailError]);

  const onSubmit = (data: IFormInput) => {
    const { confirmPassword, ...newData } = data;
    if (!emailError && confirmPassword === data.password) {
      AddUser({
        ...newData,
      })
        .unwrap()
        .then(() => {
          showSuccessMessage("User Registration Successfully");
          reset({ ...defaultFormValues });
          dispatch(
            setIsNewUser({
              isNewUser: true,
              email: newData.email,
            }),
          );
        })
        .catch(() => {
          showErrorMessage("User Registration Unsuccessful");
        });
    }
  };

  useEffect(() => {
    if (Object.keys(errors).length === 0) {
      setErrorStyle(false);
    } else {
      setErrorStyle(true);
    }
  }, [errors]);

  return (
    <Row className="mt-4">
      <Col>
        <form onSubmit={handleSubmit(onSubmit)} className="ui-stagger">
          <AppDualInput
            leftIcon="person_outline"
            leftField={{
              name: "firstName",
              placeholder: "First Name",
              register: register("firstName", {
                required: "First name is required",
                minLength: {
                  value: 2,
                  message: "This field must contain at least 2 characters",
                },
                maxLength: {
                  value: 50,
                  message:
                    "You have exceeded the maximum number of 50 characters in this field",
                },
                pattern: {
                  value: noSpecialCharsNotAllowOnlyNumbers,
                  message:
                    "Entered value can't start/end or contain only white spaces and can't contain only numbers and special characters",
                },
              }),
            }}
            rightField={{
              name: "lastName",
              placeholder: "Last Name",
              register: register("lastName", {
                required: "Last name is required",
                minLength: {
                  value: 2,
                  message: "This field must contain at least 2 characters",
                },
                maxLength: {
                  value: 50,
                  message:
                    "You have exceeded the maximum number of 50 characters in this field",
                },
                pattern: {
                  value: noSpecialCharsNotAllowOnlyNumbers,
                  message:
                    "Entered value can't start/end or contain only white spaces and can't contain only numbers and special characters",
                },
              }),
            }}
            errors={errors}
          />
          <AppInput
            className="mt-3"
            name="email"
            placeholder="Email"
            icon={
              emailRegex.test(debouncedEmail) && !emailError
                ? "check"
                : undefined
            }
            register={register("email", {
              pattern: {
                value: emailRegex,
                message: "Please enter a valid email",
              },
            })}
            errors={errors}
            isSuccess={emailRegex.test(debouncedEmail) && !emailError}
            isError={emailRegex.test(debouncedEmail) && emailError}
            leftIcon="mail"
          />
          {emailRegex.test(debouncedEmail) && emailError && (
            <p className="error">
              Email already exists. Please use a different email
            </p>
          )}
          <AppInput
            className="mt-3"
            name="password"
            icon={!showPassword ? "visibility_off" : "visibility"}
            placeholder="Password"
            onIconClick={() => setShowPassword(!showPassword)}
            type={showPassword ? "text" : "password"}
            register={register("password", {
              required: "Password is required",

              pattern: {
                value: passwordRegex,
                message:
                  "Password must have 6-20 characters and include at least one lowercase letter, one uppercase letter, one numeric and one special character",
              },
            })}
            errors={errors}
            newPassword={true}
            leftIcon="key"
          />
          <AppInput
            className="mt-3"
            name="confirmPassword"
            icon={!showConfirmPassword ? "visibility_off" : "visibility"}
            placeholder="Confirm Password"
            onIconClick={() => setShowConfirmPassword(!showConfirmPassword)}
            type={showConfirmPassword ? "text" : "password"}
            register={register("confirmPassword", {
              required: "Password confirmation is required",
              validate: (value) =>
                value === password.current || "Password doesn't match",
            })}
            errors={errors}
            leftIcon="key"
          />
          <Row>
            <Col>
              <AppButton
                text="Sign Up"
                className={`mt-4 ${errorStyle && `manual-border-shadow`}`}
                variant="blue"
                isLoading={isLoadingAddUser}
              />
            </Col>
          </Row>
          <Row>
            <Col className="mt-4 text-center">
              Already have an account?{" "}
              <Link to={AppRoute.LOGIN} className="text-link">
                Sign In
              </Link>
            </Col>
          </Row>
        </form>
      </Col>
    </Row>
  );
};

export default RegistrationForm;
